import { CtaButton } from '@deckee/api-app-main';
import { Box, Button, MessageBlock } from '@deckee/deck-hand';
import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { captureEvent } from '../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../services/feature';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { useAppDispatch } from '../../../../services/store';
import {
  claimBusinessListing,
  resetMarketplaceSelectedListing,
} from '../../../../services/store/modules/marketplace';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import MarketplaceListingCard from '../components/MarketplaceListingCard/MarketplaceListingCard';
import useDistanceFormatting from '../hooks/use-distance-formatting';
import useSelectedListing from '../hooks/use-selected-listing';

const MarketplaceListingPage: FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [selectedListing, loadingListing, error] = useSelectedListing(id);
  const [businessClaimed, setBusinessClaimed] = useState(false);
  const [claimingListing, setClaimingListing] = useState(false);
  const [claimListingError, setClaimListingError] = useState<boolean>(false);

  const {
    distanceFromRequestLocation,
    name,
    summary,
    coverImage,
    partner,
    ctaButtons,
    starRating,
    ratingsTotal,
  } = selectedListing || {};

  useEffect(
    () => () => {
      dispatch(resetMarketplaceSelectedListing());
    },
    [dispatch],
  );

  const formatDistance = useDistanceFormatting();

  const handleCtaButtonClick = async (
    ctaButton: CtaButton,
    e: MouseEvent<HTMLElement>,
  ) => {
    captureEvent({
      type: 'marketplace: cta button click',
      payload: ctaButton,
      sendToAmplitude: true,
    });

    const { url } = ctaButton;

    // Check that the url is not a 'tel:' link as in-app browser will error
    if (url.indexOf('tel:') >= 0) return;

    try {
      e.preventDefault();
      e.stopPropagation();
      await openWithInAppBrowser({ url });
    } catch (err) {
      // Something has gone wrong, just navigate as usual
    }
  };

  const handleViewOnMap = () => {
    captureEvent({
      type: 'marketplace: view listing on map',
      payload: selectedListing,
      sendToAmplitude: true,
    });

    if (selectedListing) {
      history.push(`/maps/marketplace/listing/${selectedListing.id}/summary`);
    }
  };

  const handleClaimProfile = async () => {
    captureEvent({
      type: 'marketplace: claim listing',
      payload: selectedListing,
      sendToAmplitude: true,
    });

    if (selectedListing) {
      setClaimListingError(false); // reset claim error
      setClaimingListing(true);

      try {
        await dispatch(claimBusinessListing(selectedListing.id)).unwrap();
        setBusinessClaimed(true);
      } catch (e: any) {
        setClaimListingError(true);
      }

      setClaimingListing(false);
    }
  };

  return (
    <BasicDrawerLayout
      onBack={() => history.push('/maps/marketplace/search')}
      onClose={null}
    >
      <Box p="3" data-testid="business-listing-details-page">
        {error && (
          <MessageBlock type="error" mb="3">
            {error}
          </MessageBlock>
        )}

        {loadingListing ? (
          <LogoSpinner />
        ) : (
          <>
            <MarketplaceListingCard
              name={name || ''}
              coverImage={coverImage}
              summary={summary}
              distance={
                (distanceFromRequestLocation &&
                  formatDistance(distanceFromRequestLocation)) ||
                ''
              }
              onViewOnMap={handleViewOnMap}
              partner={!!partner}
              ctaButtons={ctaButtons || []}
              onCtaButtonClick={handleCtaButtonClick}
              starRating={starRating}
              ratingsTotal={ratingsTotal || undefined}
            />

            {claimListingError && (
              <MessageBlock type="error" mt="3">
                Sorry, an unexpected error occurred! Please try again.
              </MessageBlock>
            )}
            {!claimListingError && businessClaimed && (
              <MessageBlock type="success" mt="3">
                Great! We just sent you an email with next steps to help you
                reach more customers on Deckee.
              </MessageBlock>
            )}
            {claimingListing && <LogoSpinner />}
            {!partner &&
              !businessClaimed &&
              !claimingListing &&
              hasFeatureEnabled('marketplace-claim-profile') && (
                <Box textAlign="center" mt={4}>
                  <Box mb={2}>Do you own this business?</Box>
                  <Button link onClick={handleClaimProfile}>
                    Enhance your profile
                  </Button>
                </Box>
              )}
          </>
        )}
      </Box>
    </BasicDrawerLayout>
  );
};

export default MarketplaceListingPage;
